import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { colors, fontSizes, breakpoints } from '@/styles'
import { toUnit } from '@/utils/styles'
import { Button } from '@/components/button'
import { OptionList } from './option-list'
import arrowIcon from './drop-down-arrow.svg'
import CogIcon from './cog-icon.svg'
import { show as showManageRecipientsModal } from '../add-signer/manage-recipients-modal'

const DropDownAddSignerImg = styled.img`
  width: 14px;
`

const Box = styled.div`
  height: ${props => props.height}px;
  border-radius: 6px;
  border: 1px solid ${colors.grey};
  padding: 0 10px;
  font-size: ${fontSizes.regular};
  outline: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  position: relative;
  background: ${colors.white};
  min-width: ${props => (props.minWidth ? toUnit(props.minWidth) : 'unset')};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    height: 30px;
    border-radius: 4px;
    font-size: ${fontSizes.small};
  }
`

const Icon = styled.img`
  width: 13px;
  height: 9px;
  margin-left: 10px;
  transition: transform 0.3s;

  ${({ visible }) => (visible ? 'transform: rotate(180deg);' : '')}
`

const Options = styled.div`
  position: absolute;
  max-height: ${props => (props.visible ? '9999px' : 0)};
  overflow: ${props => (props.visible ? 'visible' : 'hidden')};
  transition: all 0.5s cubic-bezier(0, 1, 0.5, 1);
  z-index: 100;
  margin-top: 5px;
  background-color: ${colors.white};
  border-radius: 6px;
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
`

const ButtonWrapper = styled.div`
  padding: 8px;
  > button {
    width: 100%;
    max-height: 32px;
    min-height: 32px;
  }
`

const Dropdown = ({
  onChange = noop,
  value,
  display = 'Select',
  minWidth,
  children,
  height,
  isText,
  contractId,
  fileMetadata,
  setFileMetadata,
  signees,
  setSignees,
  currentUser,
  setUser,
  items,
  setItems,
  currentDocument
}) => {
  const [opened, setOpened] = useState(false)
  const ref = useRef()
  const { t } = useTranslation()

  const onClickOutside = e => {
    if (ref.current.contains(e.target)) {
      return
    }
    close()
  }

  const toggle = () => {
    setOpened(curr => !curr)
  }

  const close = () => {
    setOpened(false)
  }

  const handleChange = newValue => {
    close()
    onChange(newValue)
  }

  const handleClick = () =>
    showManageRecipientsModal({
      contractId,
      fileMetadata,
      setFileMetadata,
      signees,
      setSignees,
      currentUser,
      setUser,
      items,
      setItems,
      currentDocument
    })

  return (
    <div ref={ref}>
      <Box onClick={toggle} minWidth={minWidth} height={height ? height : '32'}>
        {display}
        <Icon visible={opened} src={arrowIcon} alt="Toggle Dropdown" />
      </Box>
      <Options visible={opened}>
        <OptionList
          value={value}
          onChange={handleChange}
          clickOutsideCallback={onClickOutside}
          rounded
        >
          {children}
        </OptionList>
        {!isText && (
          <ButtonWrapper>
            <Button type="button" theme="secondary" size="small" onClick={handleClick}>
              <DropDownAddSignerImg src={CogIcon} alt="" />
              {t('l.contract_edit.dropdownmManage_recipients.button', 'Manage Recipients')}
            </Button>
          </ButtonWrapper>
        )}
      </Options>
    </div>
  )
}

Dropdown.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object
  ]),
  display: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  onChange: PropTypes.func.isRequired
}

export default Dropdown
